// Schema-aware FHIR resource differ, promoted from
// packages/react/src/components/history/ResourceDiff.tsx so non-React
// consumers (queued-edits change sets, scripts) share one diff engine.
//
// Requires the resource's data type to be loaded into Medplum's globalSchema
// (browser: `medplum.requestSchema(resourceType)`; tests:
// `indexFhirDefinitions()` from medplum-definitions.test-utils).

import {
  arrayify,
  evalFhirPathTyped,
  getSearchParameterDetails,
  type InternalSchemaElement,
  isDataTypeLoaded,
  type TypedValue,
  toTypedValue,
} from '@medplum/core'
import type { Resource, SearchParameter } from '@medplum/fhirtypes'
import { castArray } from 'lodash-es'
import { createPatch, type Operation } from 'rfc6902'

export interface ResourceFieldDiff {
  op: 'add' | 'remove' | 'replace'
  jsonPointer: string
  fhirPath: string
  property: InternalSchemaElement | undefined
  originalValue: TypedValue | undefined
  revisedValue: TypedValue | undefined
}

export function getResourceDiff({
  original,
  revised,
}: {
  original: Resource
  revised: Resource
}): ResourceFieldDiff[] {
  const resourceType = revised.resourceType ?? original.resourceType
  if (!isDataTypeLoaded(resourceType)) {
    throw new Error(
      `getResourceDiff: schema for ${resourceType} is not loaded — call medplum.requestSchema('${resourceType}') (or indexFhirDefinitions() in tests) first`
    )
  }

  const typedOriginal = [toTypedValue(original)]
  const typedRevised = [toTypedValue(revised)]
  const result: ResourceFieldDiff[] = []

  // First, we filter and consolidate the patch operations
  // We can do this because we do not use the "value" field in the patch operations
  // Remove patch operations on meta elements such as "meta.lastUpdated" and "meta.versionId"
  // Consolidate patch operations on arrays
  const patch = mergePatchOperations(createPatch(original, revised))

  // Next, convert the patch operations to a diff table
  for (const op of patch) {
    if (op.op !== 'add' && op.op !== 'remove' && op.op !== 'replace') {
      continue
    }
    const fhirPath = jsonPathToFhirPath(op.path)
    const property = tryGetElementDefinition(resourceType, fhirPath)
    const originalValue =
      op.op === 'add' ? undefined : evalFhirPathTyped(fhirPath, typedOriginal)
    const revisedValue =
      op.op === 'remove' ? undefined : evalFhirPathTyped(fhirPath, typedRevised)
    result.push({
      op: op.op,
      jsonPointer: op.path,
      fhirPath,
      property,
      originalValue: touchUpValue(property, originalValue),
      revisedValue: touchUpValue(property, revisedValue),
    })
  }

  return result
}

export function mergePatchOperations(patch: Operation[]): Operation[] {
  const result: Operation[] = []
  for (const patchOperation of patch) {
    const { op, path } = patchOperation
    if (
      path.startsWith('/meta/author') ||
      path.startsWith('/meta/compartment') ||
      path.startsWith('/meta/lastUpdated') ||
      path.startsWith('/meta/versionId')
    ) {
      continue
    }
    const count = patch.filter((el) => el.op === op && el.path === path).length
    const resultOperation = { op, path } as Operation
    if (
      count > 1 &&
      (op === 'add' || op === 'remove') &&
      /\/[0-9-]+$/.test(path)
    ) {
      // Remove everything after the last slash
      resultOperation.op = 'replace'
      resultOperation.path = path.replace(/\/[^/]+$/, '')
    }
    if (
      !result.some(
        (el) => el.op === resultOperation.op && el.path === resultOperation.path
      )
    ) {
      // Only add the operation if it doesn't already exist
      result.push(resultOperation)
    }
  }
  return result
}

export function jsonPathToFhirPath(path: string): string {
  const parts = path.split('/').filter(Boolean)
  let result = ''
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]
    if (part === '-') {
      result += '.last()'
    } else if (/^\d+$/.test(part)) {
      result += `[${part}]`
    } else {
      if (i > 0) {
        result += '.'
      }
      result += part
    }
  }

  // For attachments, remove the .url suffix
  // Note that not all ".url" properties are attachments, but it is the common case.
  // If the property is not an attachment, the diff will simply render the parent element,
  // which is still fine.
  if (result.endsWith('.url')) {
    result = result.replace(/\.url$/, '')
  }

  return result
}

export function tryGetElementDefinition(
  resourceType: string,
  fhirPath: string
): InternalSchemaElement | undefined {
  try {
    const details = getSearchParameterDetails(resourceType, {
      resourceType: 'SearchParameter',
      base: [resourceType],
      code: `${resourceType}.${fhirPath}`,
      expression: `${resourceType}.${fhirPath}`,
    } as SearchParameter)
    return details?.elementDefinitions?.[0]
  } catch {
    // Paths outside the schema (contained resources, choice expansions, …)
    // have no element definition — the diff row just renders untyped.
    return undefined
  }
}

export function touchUpValue(
  property: InternalSchemaElement | undefined,
  input: TypedValue[] | TypedValue | undefined
): TypedValue | undefined {
  if (!input) {
    return undefined
  }

  const inputArray = castArray(input)
  if (inputArray.length === 0) {
    return undefined
  }

  return {
    type: inputArray[0].type,
    value: fixArray(input, !!property?.isArray),
  }
}

function fixArray(input: TypedValue[] | TypedValue, isArray: boolean): any {
  const inputValue = (arrayify(input) as TypedValue[]).flatMap((v) => v.value)
  return isArray ? inputValue : inputValue[0]
}
